import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import {
  useCreateOrderTemplateMutation,
  useGetOrderTemplatesFiltersQuery,
  useGetTypesFiltersQuery,
  useGetUnitMeasuresQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { useDebouncedValue } from '@mantine/hooks';
import { ReactComponent as ArrowSvg } from 'assets/img/filter/arrow.svg';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { ReactComponent as KebabMenu } from './img/iconKebab.svg';
import { ReactComponent as CalendarSvg } from 'assets/img/filter/iconCalendarBig.svg';
import { ReactComponent as IconChewronDown } from './img/iconChevronDown.svg';
import { ReactComponent as IconChewronUp } from './img/iconChevronUp.svg';
import { ReactComponent as IconCloseGray } from './img/iconCloseGray.svg';
import {
  NumberInput,
  NumberInputHandlers,
  Select,
  Textarea,
  Popover,
  Stack,
  Checkbox as MantineCheckbox,
  Group,
  Switch,
  TextInput,
  Loader,
  Box,
} from '@mantine/core';
import { AddressDadata2 } from 'components/AddressDadata2';
import ContentLoader from 'react-content-loader';
import styles from './CreateOrderForm.module.scss';
import { DateInput, DatePickerInput } from '@mantine/dates';
import moment from 'moment';
import { OrdersTemplates } from 'components/OrderTemplates';
import { Button } from 'ui/Button';
import { AppTooltip } from 'ui/AppTooltip';
import { Modal } from 'components/Modal';
import { ReactComponent as IconCheckSvg } from './img/iconDone.svg';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import {
  OrderAmountErrorMessage,
  useOrderAmountError,
} from 'components/OrderAmountError';
import { ReactComponent as IconRuble } from './img/iconRuble.svg';
import {
  CreateOrderFormProps,
  HandleFieldKey,
  HandleFieldValue,
  isServiceTypeArray,
  isTypesOfServicesResponse,
  isCreateOrderTemplateResponse,
} from './types';
import { TextError } from 'ui/TextError/TextError';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { normalizeUnitPrice } from 'utils/normalizeUnitPrice';
import { useGetTypeContractQuery } from 'redux/Api/contractsApi/contractsApi';

export const Label: React.FC<{ text: string }> = ({ text }) => {
  return <p className={styles.label}>{text}</p>;
};

export const QuantityStepperControls: React.FC<{
  onIncrement: () => void;
  onDecrement: () => void;
  decrementDisabled?: boolean;
}> = ({ onIncrement, onDecrement, decrementDisabled }) => (
  <div className={styles.quantityControls}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
      }}>
      <button
        type='button'
        className={styles.quantityControlBtn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onIncrement}
        aria-label='Увеличить объем'>
        <IconChewronUp />
      </button>
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
      }}>
      <button
        type='button'
        className={styles.quantityControlBtn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onDecrement}
        disabled={decrementDisabled}
        aria-label='Уменьшить объем'>
        <IconChewronDown />
      </button>
    </div>
  </div>
);

export const CreateOrderForm: React.FC<CreateOrderFormProps> = ({
  handleFieldChange,
  errors,
  selectedWorker = null,
  clearErrorForField,
  setErrorCountDescription,
}) => {
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [error, setError] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [hasDescriptionScroll, setHasDescriptionScroll] = useState(false);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const descriptionResizeRef = useRef<HTMLDivElement>(null);
  const descriptionSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const { data: dataProfile } = useGetProfileQuery();
  const { data: typeContract, isLoading: isLoadingTypeContract } =
    useGetTypeContractQuery(undefined, {
      skip: !dataProfile?.settings?.tetrika,
    });

  const selectContractKind = useMemo(
    () =>
      typeContract?.map((item) => ({
        value: item.type,
        label: item.title,
      })) ?? [],
    [typeContract],
  );

  const { data: unitMeasuresData, isLoading: isLoadingUnitMeasures } =
    useGetUnitMeasuresQuery();

  const transformedUnitMeasuresData = useMemo(() => {
    if (!unitMeasuresData) return [];
    return Object.entries(unitMeasuresData)
      .map(([key, value]) => ({
        value: key,
        label: value,
      }))
      .sort((a, b) => {
        if (a.value === 'service') return -1;
        if (b.value === 'service') return 1;
        return 0;
      });
  }, [unitMeasuresData]);

  useEffect(() => {
    if (transformedUnitMeasuresData.length > 0 && !infoOrderDetail.unit_measure) {
      changeField('unit_measure', transformedUnitMeasuresData[0].value);
    }
  }, [transformedUnitMeasuresData]);

  const { data: profileData } = useGetProfileQuery();

  const descriptionValue = infoOrderDetail?.description || '';
  const valueCounterError = descriptionValue.length > 3000;

  useEffect(() => {
    setErrorCountDescription?.(valueCounterError);
  }, [valueCounterError, setErrorCountDescription]);

  useEffect(() => {
    const MIN_DESCRIPTION_HEIGHT = 150;

    const measureContentHeight = (textarea: HTMLTextAreaElement) => {
      const prevHeight = textarea.style.getPropertyValue('height');
      const prevPriority = textarea.style.getPropertyPriority('height');

      textarea.style.setProperty('height', '0px', 'important');
      const contentHeight = textarea.scrollHeight;

      if (prevHeight) {
        textarea.style.setProperty('height', prevHeight, prevPriority);
      } else {
        textarea.style.removeProperty('height');
      }

      return contentHeight;
    };

    const updateDescriptionScroll = () => {
      const textarea = descriptionTextareaRef.current;
      if (!textarea) return;

      const contentHeight = measureContentHeight(textarea);
      const needsScroll = contentHeight > MIN_DESCRIPTION_HEIGHT;

      if (document.activeElement === textarea) {
        descriptionSelectionRef.current = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
      }
      setScrollHeight(contentHeight);
      setHasDescriptionScroll(needsScroll);
    };

    const frameId = requestAnimationFrame(updateDescriptionScroll);
    window.addEventListener('resize', updateDescriptionScroll);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateDescriptionScroll);
    };
  }, [descriptionValue]);

  useEffect(() => {
    const wrap = descriptionResizeRef.current;
    const textarea = descriptionTextareaRef.current;
    if (!wrap || !textarea) return;

    if (hasDescriptionScroll) {
      const contentHeight = Math.min(500, Math.max(150, scrollHeight));
      const currentHeight = wrap.offsetHeight || contentHeight;
      const height = Math.min(500, Math.max(150, Math.min(currentHeight, contentHeight)));
      wrap.style.height = `${height}px`;
      wrap.style.maxHeight = `${contentHeight}px`;
    } else {
      wrap.style.height = '';
      wrap.style.maxHeight = '';
    }

    const selection = descriptionSelectionRef.current;
    if (selection) {
      descriptionSelectionRef.current = null;
      requestAnimationFrame(() => {
        const el = descriptionTextareaRef.current;
        if (!el) return;
        el.focus({ preventScroll: true });
        el.setSelectionRange(selection.start, selection.end);
      });
    }
  }, [hasDescriptionScroll, scrollHeight]);

  const changeField = (key: HandleFieldKey, value: HandleFieldValue) => {
    handleFieldChange(key, value);
    clearErrorForField?.(key);
  };

  useEffect(() => {
    if (!dataProfile?.settings?.tetrika || infoOrderDetail.contract_kind) return;
    if (!selectContractKind.length) return;

    const defaultContractKind =
      selectContractKind.find((item) => item.value === 'oferta_accept')?.value ||
      selectContractKind[0].value;

    changeField('contract_kind', defaultContractKind);
  }, [dataProfile?.settings?.tetrika, infoOrderDetail.contract_kind, selectContractKind]);

  const syncTotalPrice = (totalPrice: string | number, unit_volume: number | string) => {
    if (totalPrice === '' || totalPrice === null || totalPrice === undefined) {
      handleFieldChange('total_price', '');
      return;
    }
    const price = +totalPrice;
    const qty = +unit_volume || 1;
    handleFieldChange('total_price', Math.round(price * qty * 100) / 100);
  };

  const [typeSearchInput, setTypeSearchInput] = useState('');
  const [typeSearchQuery, setTypeSearchQuery] = useState('');
  const [errorSaveTemplate, setErrorSaveTemplate] = useState('');
  const [debouncedTypeSearch] = useDebouncedValue(typeSearchQuery, 300);
  const [typesDropdownOpen, setTypesDropdownOpen] = useState(false);
  const [typesPage, setTypesPage] = useState(1);
  const typesViewportRef = useRef<HTMLDivElement>(null);
  const typesLoadMoreLockRef = useRef(false);
  const suppressTypeSearchRef = useRef(false);
  const prevDebouncedTypeSearchRef = useRef(debouncedTypeSearch);
  const unitHandlersRef = useRef<NumberInputHandlers>(null);

  const typesApiPage =
    prevDebouncedTypeSearchRef.current !== debouncedTypeSearch ? 1 : typesPage;

  if (prevDebouncedTypeSearchRef.current !== debouncedTypeSearch) {
    prevDebouncedTypeSearchRef.current = debouncedTypeSearch;
  }

  useEffect(() => {
    setTypesPage(1);
  }, [typeSearchQuery]);

  const {
    data: typesDataRaw,
    isLoading,
    isFetching: isFetchingTypes,
  } = useGetTypesFiltersQuery({
    page: typesApiPage,
    per_page: 20,
    ...(typesDropdownOpen && debouncedTypeSearch ? { search: debouncedTypeSearch } : {}),
  });

  const dataTypes = isTypesOfServicesResponse(typesDataRaw) ? typesDataRaw : undefined;
  const serviceItems =
    dataTypes?.items && isServiceTypeArray(dataTypes.items) ? dataTypes.items : [];
  const hasNextTypesPage =
    !!dataTypes?.meta && dataTypes.meta.current_page < dataTypes.meta.last_page;

  const loadMoreTypes = useCallback(() => {
    if (isFetchingTypes || !hasNextTypesPage || typesLoadMoreLockRef.current) return;
    typesLoadMoreLockRef.current = true;
    setTypesPage((prev) => prev + 1);
  }, [isFetchingTypes, hasNextTypesPage]);

  useEffect(() => {
    if (!isFetchingTypes) {
      typesLoadMoreLockRef.current = false;
    }
  }, [isFetchingTypes]);

  const handleTypesScroll = useCallback(() => {
    const el = typesViewportRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gapToBottom < 72 && el.scrollTop > 12) {
      loadMoreTypes();
    }
  }, [loadMoreTypes]);

  useEffect(() => {
    if (infoOrderDetail.unit_price && infoOrderDetail.unit_volume) {
      handleFieldChange(
        'total_price',
        Number(infoOrderDetail.unit_price) * Number(infoOrderDetail.unit_volume),
      );
    }
  }, [infoOrderDetail.unit_volume, infoOrderDetail.unit_price]);

  const handleTypesDropdownOpen = useCallback(() => {
    setTypesDropdownOpen(true);
    suppressTypeSearchRef.current = true;
    setTypeSearchInput('');
    setTypeSearchQuery('');
  }, []);

  const handleTypesDropdownClose = useCallback(() => {
    suppressTypeSearchRef.current = true;
    setTypesDropdownOpen(false);
    setTypeSearchInput('');
    setTypeSearchQuery('');
  }, []);

  const handleTypeSearchChange = useCallback(
    (value: string) => {
      const selectedLabel = infoOrderDetail.order_type?.trim() ?? '';

      if (!typesDropdownOpen) {
        return;
      }

      if (suppressTypeSearchRef.current) {
        suppressTypeSearchRef.current = false;
        if (!value.trim() || value === selectedLabel) {
          return;
        }
      }

      setTypeSearchInput(value);
      setTypeSearchQuery(value.trim());
      setTypesPage(1);
    },
    [typesDropdownOpen, infoOrderDetail.order_type],
  );

  const { infoPercent } = useSelector((state: RootState) => state.authSlice);
  const [createOrderTemplate, { isLoading: isLoadingCreate }] =
    useCreateOrderTemplateMutation();
  const [showNotification, setShowNotification] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const getValidDate = (dateValue: string | Date | null | undefined): Date => {
    if (!dateValue) return moment().startOf('day').toDate();

    let date;
    if (typeof dateValue === 'string' && dateValue.includes(' ')) {
      const [datePart] = dateValue.split(' ');
      date = moment(datePart, 'YYYY-MM-DD').startOf('day');
    } else {
      date = moment(dateValue).startOf('day');
    }

    const today = moment().startOf('day');

    return date.isBefore(today) ? today.toDate() : date.toDate();
  };

  const [value, setValue] = useState<[Date | null, Date | null]>([null, null]);

  useEffect(() => {
    const startDate = infoOrderDetail.date_start
      ? getValidDate(infoOrderDetail.date_start)
      : null;
    const endDate = infoOrderDetail.date_end
      ? getValidDate(infoOrderDetail.date_end)
      : null;
    setValue([startDate, endDate]);
  }, [infoOrderDetail.date_start, infoOrderDetail.date_end]);

  useEffect(() => {
    const city = infoOrderDetail.city?.trim() || '';
    const street = infoOrderDetail.street?.trim() || '';
    const home = infoOrderDetail.home?.trim() || '';
    const hasAddress = city.length > 0 || street.length > 0 || home.length > 0;

    if (
      hasAddress &&
      infoOrderDetail.with_address !== false &&
      infoOrderDetail.is_remote !== 'Удаленно'
    ) {
      handleFieldChange('with_address', true);
      handleFieldChange('is_remote', 'По адресу');
    }

    if (infoOrderDetail.date_start) {
      handleFieldChange('with_date_start', true);
    }

    if (infoOrderDetail.date_end) {
      handleFieldChange('with_date_end', true);
    }

    if (infoOrderDetail.date_start && infoOrderDetail.date_end) {
      setValue([
        getValidDate(infoOrderDetail.date_start),
        getValidDate(infoOrderDetail.date_end),
      ]);
    }
  }, [
    infoOrderDetail.city,
    infoOrderDetail.street,
    infoOrderDetail.home,
    infoOrderDetail.date_start,
    infoOrderDetail.date_end,
    infoOrderDetail.with_address,
    infoOrderDetail.is_remote,
  ]);

  const withStartDate = infoOrderDetail.with_date_start;
  const withEndDate = infoOrderDetail.with_date_end;

  const selectData = useMemo(() => {
    const items = serviceItems.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
    const isTypeSearchActive = typeSearchQuery.trim().length > 0;
    const selectedId = infoOrderDetail.order_type_id?.toString();
    if (
      !isTypeSearchActive &&
      selectedId &&
      infoOrderDetail.order_type &&
      !items.some((item) => item.value === selectedId)
    ) {
      items.unshift({
        value: selectedId,
        label: infoOrderDetail.order_type,
      });
    }
    return items;
  }, [
    serviceItems,
    infoOrderDetail.order_type_id,
    infoOrderDetail.order_type,
    typeSearchQuery,
  ]);

  useEffect(() => {
    if (infoOrderDetail.date_start) {
      const validStartDate = getValidDate(infoOrderDetail.date_start);
      if (
        moment(validStartDate).format('YYYY-MM-DD') !==
        moment(infoOrderDetail.date_start).format('YYYY-MM-DD')
      ) {
        handleFieldChange('date_start', validStartDate);
      }
    }

    if (infoOrderDetail.date_end) {
      const validEndDate = getValidDate(infoOrderDetail.date_end);
      if (
        moment(validEndDate).format('YYYY-MM-DD') !==
        moment(infoOrderDetail.date_end).format('YYYY-MM-DD')
      ) {
        handleFieldChange('date_end', validEndDate);
      }
    }
  }, [infoOrderDetail.date_start, infoOrderDetail.date_end]);
  const [debouncedTemplateNameCheck] = useDebouncedValue(templateName.trim(), 300);
  const { data: templatesCheckData } = useGetOrderTemplatesFiltersQuery(
    {
      page: 1,
      per_page: 20,
      search: debouncedTemplateNameCheck,
    },
    { skip: !showSaveTemplateModal || !debouncedTemplateNameCheck },
  );
  const templateNameExists = (templatesCheckData?.data || []).some(
    (template) => template.name === templateName.trim(),
  );

  const handleSaveTemplate = async () => {
    const {
      order_type_id,
      description,
      is_remote,
      accept_automatically,
      city,
      street,
      home,
      apartment,
      add_tax_amount,
      date_start,
      date_end,
      with_date_start,
      with_date_end,
      use_fast_withdraw,
      unit_price,
      unit_volume,
      unit_measure,
      is_unit_based,
      unit_volume_min,
      unit_volume_max,
    } = infoOrderDetail;

    if (valueCounterError) return;
    setErrorSaveTemplate('');

    try {
      const result = await createOrderTemplate({
        name: templateName,
        order_type_id,
        description,
        is_remote: is_remote === 'Удаленно',
        accept_automatically,
        city,
        street,
        home,
        apartment,
        add_tax_amount,
        use_fast_withdraw,
        date_end: with_date_end ? `${moment(date_end).format('YYYY-MM-DD 00:00')}` : null,
        date_start: with_date_start
          ? `${moment(date_start).format('YYYY-MM-DD 00:00')}`
          : null,
        unit_volume: unit_volume as number | string,
        unit_volume_min: unit_volume_min as number | string,
        unit_volume_max: unit_volume_max as number | string,
        unit_price: normalizeUnitPrice(unit_price),
        unit_measure: unit_measure,
        is_unit_based: is_unit_based,
      }).unwrap();

      if (isCreateOrderTemplateResponse(result)) {
        handleFieldChange('order_preset_id', result.id);
      }

      setTemplateName('');
      setShowSaveTemplateModal(false);

      setShowNotification(true);
    } catch (error) {
      setErrorSaveTemplate(
        (error as { data?: { message?: string } }).data?.message || '',
      );
      console.log('Error saving template:', error);
    }
  };

  useEffect(() => {
    if (serviceItems.length === 0) return;

    if (infoOrderDetail.order_type && !infoOrderDetail.order_type_id) {
      const matchingType = serviceItems.find(
        (item) => item.name === infoOrderDetail.order_type,
      );
      if (matchingType) {
        handleFieldChange('order_type_id', matchingType.id);
      }
    }
  }, [handleFieldChange, infoOrderDetail, serviceItems]);

  const selectedTypeValue =
    Number(infoOrderDetail.order_type_id) > 0
      ? infoOrderDetail.order_type_id.toString()
      : null;

  const handleTogglePercent = () => {
    const currentAmount = +infoOrderDetail.unit_price * infoOrderDetail.unit_volume;

    if (!infoOrderDetail.add_tax_amount) {
      const withTax = currentAmount * (1 + 0.06 / 0.94);
      const roundedAmount = Math.round(withTax * 100) / 100;
      handleFieldChange('unit_price', roundedAmount / infoOrderDetail.unit_volume);
      syncTotalPrice(roundedAmount, infoOrderDetail.unit_volume || 1);
    } else {
      const withoutTax = currentAmount / (1 + 0.06 / 0.94);
      const roundedAmount = Math.round(withoutTax * 100) / 100;
      handleFieldChange('unit_price', roundedAmount / infoOrderDetail.unit_volume);
      syncTotalPrice(roundedAmount, infoOrderDetail.unit_volume || 1);
    }
    handleFieldChange('add_tax_amount', !infoOrderDetail.add_tax_amount);
  };

  const handleToggleSelling = () => {
    if (infoOrderDetail.add_tax_amount) {
      handleTogglePercent();
    }
    handleFieldChange('accept_automatically', 0);
    handleFieldChange('is_unit_based', !infoOrderDetail.is_unit_based);
  };

  const orderAmountInput = {
    unitPrice: +infoOrderDetail.unit_price,
    totalPrice: +infoOrderDetail.total_price,
    unitVolume: infoOrderDetail.unit_volume,
    useFastWithdraw: !!infoOrderDetail.use_fast_withdraw,
    amountError: errors.amount,
    selectedWorker,
    isUnitBased: infoOrderDetail.is_unit_based,
  };

  const { sumToBePaid, message: orderAmountMessage } =
    useOrderAmountError(orderAmountInput);

  const handleDateChange = (date: [Date | null, Date | null]) => {
    const today = moment().startOf('day').toDate();
    const startDate = date[0]
      ? moment(date[0]).isBefore(moment(today))
        ? today
        : date[0]
      : null;
    const endDate = date[1]
      ? moment(date[1]).isBefore(moment(today))
        ? today
        : date[1]
      : null;
    const finalEndDate =
      endDate && startDate
        ? moment(endDate).isBefore(startDate)
          ? startDate
          : endDate
        : endDate;
    setValue([startDate, finalEndDate]);
    handleFieldChange('date_start', startDate);
    handleFieldChange('date_end', finalEndDate);
  };

  const syncUnitVolumeRangeFromMin = useCallback(
    (minValue: string | number = infoOrderDetail.unit_volume_min) => {
      const max = Number(infoOrderDetail.unit_volume_max);
      const min = Number(minValue);

      if (max > 0 && min > max) {
        handleFieldChange('unit_volume_max', String(minValue));
      }
    },
    [infoOrderDetail.unit_volume_max, infoOrderDetail.unit_volume_min, handleFieldChange],
  );

  const syncUnitVolumeRangeFromMax = useCallback(
    (maxValue: string | number = infoOrderDetail.unit_volume_max) => {
      const max = Number(maxValue);
      const min = Number(infoOrderDetail.unit_volume_min);

      if (max > 0 && min > max) {
        handleFieldChange('unit_volume_min', String(maxValue));
      }
    },
    [infoOrderDetail.unit_volume_max, infoOrderDetail.unit_volume_min, handleFieldChange],
  );

  return (
    <div className={styles.order}>
      <div className={styles.order_inner}>
        <div className={styles.order_inner__header}>
          <p className={styles.order_inner__title}>Условия заказа</p>
          <div className={styles.order_inner__wrapper}>
            {!infoOrderDetail.order_preset_id && (
              <Button
                theme='link'
                onClick={() => {
                  setShowSaveTemplateModal(true);
                  setErrorSaveTemplate('');
                }}>
                Сохранить шаблон
              </Button>
            )}
            <Popover width={300} position='bottom-end' radius={'4px'}>
              <Popover.Target>
                <KebabMenu style={{ cursor: 'pointer' }} />
              </Popover.Target>
              <Popover.Dropdown
                p='4px 0'
                style={{
                  boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                  border: 'none',
                }}>
                <Stack gap='0'>
                  <Group p='10px 16px' className={styles.item}>
                    <MantineCheckbox
                      color='#002CFB'
                      size='16px'
                      checked={withStartDate}
                      onChange={(e) => {
                        handleFieldChange('with_date_start', e.target.checked);

                        if (e.target.checked) {
                          handleFieldChange('date_start', getValidDate(new Date()));
                        } else {
                          handleFieldChange('date_start', null);
                        }
                      }}
                      label='Дата начала заказа'
                      styles={{
                        label: {
                          fontSize: '16px',
                          lineHeight: '22px',
                        },
                        inner: {
                          height: '22px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    />
                  </Group>
                  <Group p='10px 16px' className={styles.item}>
                    <MantineCheckbox
                      color='#002CFB'
                      size='16px'
                      checked={withEndDate}
                      onChange={(e) => {
                        handleFieldChange('with_date_end', e.target.checked);

                        if (e.target.checked) {
                          handleFieldChange('date_end', getValidDate(new Date()));
                        } else {
                          handleFieldChange('date_end', null);
                        }
                      }}
                      label='Дата завершения заказа'
                      styles={{
                        label: {
                          fontSize: '16px',
                          lineHeight: '22px',
                        },
                        inner: {
                          height: '22px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    />
                  </Group>
                  <Group p='10px 16px' className={styles.item}>
                    <MantineCheckbox
                      color='#002CFB'
                      size='16px'
                      checked={infoOrderDetail.with_address}
                      onChange={(e) => {
                        if (!infoOrderDetail.with_address) {
                          handleFieldChange('is_remote', 'По адресу');
                        } else {
                          handleFieldChange('is_remote', 'Удаленно');
                          handleFieldChange('apartment', '');
                          handleFieldChange('city', '');
                          handleFieldChange('street', '');
                          handleFieldChange('home', '');
                        }
                        handleFieldChange('with_address', e.target.checked);
                      }}
                      label='Адрес'
                      styles={{
                        label: {
                          fontSize: '16px',
                          lineHeight: '22px',
                        },
                        inner: {
                          height: '22px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    />
                    <AppTooltip label={'По умолчанию выбран удаленный формат'}>
                      <InfoIcon />
                    </AppTooltip>
                  </Group>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </div>
        </div>
        <OrdersTemplates />

        <div>
          <div>
            <Label text='Описание заказа' />
            <div
              ref={descriptionResizeRef}
              className={
                hasDescriptionScroll
                  ? `${styles.descriptionResize} ${styles.descriptionResize_active}`
                  : styles.descriptionResize
              }>
              <Textarea
                ref={descriptionTextareaRef}
                classNames={{
                  root: hasDescriptionScroll ? styles.descriptionTextareaRoot : undefined,
                  input: styles.descriptionTextareaInput,
                }}
                placeholder='Введи описание'
                value={infoOrderDetail.description || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  changeField('description', e.target.value);
                }}
                error={errors.description}
                withErrorStyles={false}
                resize='none'
                rows={1}
                autosize={!hasDescriptionScroll}
                minRows={1}
                maxRows={5}
                styles={{
                  input: {
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '12px',
                    paddingBottom: '11px',
                    border:
                      errors.description || valueCounterError
                        ? '1px solid #e10d0d'
                        : 'none',
                  },
                  error: { fontSize: '14px', color: '#e10d0d' },
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <a
              href={`${(process.env.REACT_APP_BASE_URL || '/').replace(
                /\/$/,
                '',
              )}/knowledge/kb/Kak-pravilno-zapolnyat-opisanie-zakaza-60`}
              target='_blank'
              rel='noreferrer'
              style={{
                textDecoration: 'underline',
                color: '#002CFB',
                cursor: 'pointer',
              }}>
              Как правильно писать?
            </a>
            <p
              style={{
                display: 'flex',
                alignItems: 'start',
                color: valueCounterError ? 'red' : '#71869D',
                paddingTop: '4px',
              }}>
              {descriptionValue.length}/3000
            </p>
          </div>
        </div>

        {dataProfile?.settings?.tetrika && (
          <div>
            <Label text='Тип договора' />
            {isLoadingTypeContract ? (
              <ContentLoader
                speed={2}
                width={'100%'}
                height={'48px'}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : (
              <Select
                data={selectContractKind || []}
                nothingFoundMessage='Ничего не найдено'
                placeholder='Выбери тип договора'
                styles={{
                  input: {
                    height: 48,
                    fontSize: '16px',
                    border: errors.contract_kind ? '1px solid red' : 'none',
                    padding: '0 16px',
                    cursor: 'pointer',
                  },
                  option: {
                    minHeight: '48px',
                    height: 'auto',
                    fontSize: '16px',
                    padding: '12px 16px',
                    lineHeight: '1.4',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  },
                  dropdown: {
                    boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                    border: 'none',
                    borderRadius: 4,
                    padding: 0,
                  },
                  section: {
                    marginRight: '6px',
                  },
                }}
                comboboxProps={{
                  position: 'bottom',
                  middlewares: { flip: false, shift: false },
                  offset: 4,
                }}
                value={infoOrderDetail.contract_kind ?? null}
                onChange={(val) => {
                  if (!val) return;
                  changeField('contract_kind', val);
                  clearErrorForField?.('contract_kind');
                }}
                withCheckIcon={false}
                rightSection={<ArrowSvg />}
                allowDeselect={false}
              />
            )}
            {errors.contract_kind ? (
              <p className={styles.errorPlaceholder}>{errors.contract_kind}</p>
            ) : null}
          </div>
        )}

        <div>
          <Label text='Услуга' />
          {isLoading ? (
            <ContentLoader
              speed={2}
              width={'100%'}
              height={'48px'}
              backgroundColor={'#FFFFFF'}
              foregroundColor={'#D8E4FB8C'}>
              <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
            </ContentLoader>
          ) : (
            <Select
              data={selectData || []}
              placeholder='Выбери услугу'
              searchable
              searchValue={typesDropdownOpen ? typeSearchInput : undefined}
              onSearchChange={handleTypeSearchChange}
              onDropdownOpen={handleTypesDropdownOpen}
              onDropdownClose={handleTypesDropdownClose}
              filter={({ options }) => options}
              nothingFoundMessage='Ничего не найдено'
              scrollAreaProps={{
                viewportRef: typesViewportRef,
                onScrollPositionChange: handleTypesScroll,
              }}
              styles={{
                input: {
                  height: '48px',
                  fontSize: '16px',
                  border: errors.type ? '1px solid #e10d0d' : 'none',
                },
                option: {
                  minHeight: '48px',
                  height: 'auto',
                  fontSize: '16px',
                  padding: '12px 16px',
                  lineHeight: '1.4',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                },
              }}
              value={selectedTypeValue}
              onChange={(val) => {
                if (!val) return;
                const selectedType = selectData.find((item) => item.value === val);
                changeField('order_type_id', Number(val));
                changeField('order_type', selectedType ? selectedType.label : '');
                setTypeSearchInput('');
                setTypeSearchQuery('');
                suppressTypeSearchRef.current = true;
                requestAnimationFrame(() => {
                  (document.activeElement as HTMLElement | null)?.blur?.();
                });
              }}
              withCheckIcon={false}
              rightSection={
                infoOrderDetail.order_type_id || typeSearchInput ? (
                  <Box
                    component='button'
                    type='button'
                    onPointerDown={(e: React.PointerEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFieldChange('order_type_id', null);
                      handleFieldChange('order_type', '');
                      setTypeSearchInput('');
                      setTypeSearchQuery('');
                      suppressTypeSearchRef.current = true;
                    }}
                    onTouchStart={(e: React.TouchEvent) => {
                      e.stopPropagation();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0,
                      border: 'none',
                      background: 'none',
                    }}>
                    <IconCloseGray />
                  </Box>
                ) : isFetchingTypes && hasNextTypesPage ? (
                  <Loader size='xs' color='#002CFB' />
                ) : (
                  <ArrowSvg />
                )
              }
              rightSectionPointerEvents='auto'
              allowDeselect={false}
              comboboxProps={{
                position: 'bottom',
                middlewares: { flip: false, shift: false },
                offset: 4,
              }}
            />
          )}
          {errors.type ? <p className={styles.errorPlaceholder}>{errors.type}</p> : null}
        </div>

        {withStartDate && !withEndDate && (
          <div>
            <Label text='Дата начала' />
            <DateInput
              value={
                infoOrderDetail?.date_start
                  ? getValidDate(infoOrderDetail.date_start)
                  : null
              }
              onChange={(value) => {
                handleFieldChange('date_start', value);
              }}
              placeholder={moment(new Date()).format('DD.MM.YYYY')}
              rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
              rightSectionPointerEvents='none'
              styles={{
                input: { height: '8px', fontSize: '16px', border: 'none' },
              }}
              flex='1'
              minDate={new Date()}
              highlightToday
              valueFormat={'DD.MM.YYYY'}
            />
          </div>
        )}
        {withEndDate && !withStartDate && (
          <div>
            <Label text='Дата завершения' />
            <DateInput
              value={
                infoOrderDetail?.date_end ? getValidDate(infoOrderDetail.date_end) : null
              }
              onChange={(value) => {
                handleFieldChange('date_end', value);
              }}
              placeholder={moment(new Date()).format('DD.MM.YYYY')}
              rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
              rightSectionPointerEvents='none'
              styles={{
                input: { height: '48px', fontSize: '16px', border: 'none' },
              }}
              flex={1}
              highlightToday
              minDate={new Date()}
              valueFormat={'DD.MM.YYYY'}
            />
          </div>
        )}
        {withStartDate && withEndDate && (
          <div>
            <Label text='Период' />
            <DatePickerInput
              styles={{
                input: { height: '48px', fontSize: '16px', border: 'none' },
              }}
              type='range'
              placeholder='Период'
              value={value}
              onChange={handleDateChange}
              rightSection={<CalendarSvg style={{ height: '16px', width: '16px' }} />}
              rightSectionPointerEvents='none'
              valueFormat={'DD.MM.YYYY'}
              minDate={new Date()}
              allowSingleDateInRange
              labelSeparator='–'
            />
          </div>
        )}
        {infoOrderDetail.with_address && (
          <div>
            <Label text='Адрес' />
            <AddressDadata2
              initialAddress={{
                city: infoOrderDetail.city,
                street: infoOrderDetail.street,
                home: infoOrderDetail.home,
                apartment: infoOrderDetail.apartment,
              }}
            />
            {errors.address && (
              <p className={styles.errorPlaceholder}>{errors.address}</p>
            )}
          </div>
        )}

        {profileData?.features?.unitbased && (
          <Group gap='8px' h='40px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                  cursor: 'pointer',
                },
                track: {
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: infoOrderDetail.is_unit_based
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={infoOrderDetail.is_unit_based}
              onChange={() => {
                handleToggleSelling();
              }}
              label='Оплата за выполненный объем'
            />
            <AppTooltip label='Укажи цену за единицу. Итоговая сумма будет рассчитываться при завершении заказа на основе выполненного объема'>
              <InfoIcon style={{ cursor: 'pointer' }} />
            </AppTooltip>
          </Group>
        )}
        <div>
          <div className={styles.amount}>
            <div>
              <Label text='Цена за ед.' />
              <NumberInput
                flex={1}
                placeholder={'0'}
                value={
                  infoOrderDetail.unit_price != null
                    ? String(infoOrderDetail.unit_price)
                    : ''
                }
                rightSection={<IconRuble />}
                onChange={(val) => {
                  changeField('unit_price', String(val));
                  handleFieldChange('add_tax_amount', false);
                  syncTotalPrice(val, infoOrderDetail.unit_volume || 1);
                }}
                allowNegative={false}
                decimalScale={2}
                hideControls={true}
                styles={{
                  input: {
                    height: '46px',
                    fontSize: '16px',
                    border: errors.amount ? '1px solid #e10d0d' : 'none',
                  },
                  error: { fontSize: '14px', color: '#e10d0d' },
                }}
              />
            </div>
            <div>
              <Label text='Объем' />
              <NumberInput
                flex={1}
                placeholder='1'
                allowNegative={false}
                decimalScale={0}
                hideControls
                handlersRef={unitHandlersRef}
                value={Number(infoOrderDetail.unit_volume || 1)}
                onChange={(val) => {
                  const unitValue = val === '' || val === null ? 1 : Number(val);
                  const unit_volume = Math.max(1, unitValue);
                  changeField('unit_volume', unit_volume);
                  syncTotalPrice(infoOrderDetail.unit_price, unit_volume);
                }}
                min={1}
                startValue={1}
                rightSectionPointerEvents='auto'
                rightSection={
                  <QuantityStepperControls
                    onIncrement={() => unitHandlersRef.current?.increment()}
                    onDecrement={() => unitHandlersRef.current?.decrement()}
                    decrementDisabled={Number(infoOrderDetail.unit_volume || 1) <= 1}
                  />
                }
                styles={{
                  input: {
                    height: '46px',
                    fontSize: '16px',
                    border: 'none',
                  },
                  error: { fontSize: '14px', color: '#e10d0d' },
                }}
              />
            </div>
            <div className={styles.amount_unit}>
              <Label text='Ед. изм-я' />
              {isLoadingUnitMeasures ? (
                <ContentLoader
                  speed={2}
                  width={'100%'}
                  height={'48px'}
                  backgroundColor={'#FFFFFF'}
                  foregroundColor={'#D8E4FB8C'}>
                  <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                </ContentLoader>
              ) : (
                <Select
                  data={transformedUnitMeasuresData}
                  nothingFoundMessage='Ничего не найдено'
                  maxDropdownHeight={160}
                  styles={{
                    input: {
                      height: '46px',
                      fontSize: '16px',
                      border: 'none',
                      cursor: 'pointer',
                    },
                    option: {
                      minHeight: '46px',
                      height: 'auto',
                      fontSize: '16px',
                      padding: '12px 16px',
                      lineHeight: '1.4',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    },
                  }}
                  value={
                    transformedUnitMeasuresData?.find(
                      (item) =>
                        item.label === infoOrderDetail.unit_measure ||
                        item.value === infoOrderDetail.unit_measure?.toString(),
                    )?.value ??
                    transformedUnitMeasuresData[0]?.value ??
                    null
                  }
                  onChange={(val) => {
                    const selected = transformedUnitMeasuresData?.find(
                      (item) => item.value === val,
                    );
                    changeField('unit_measure', selected?.value);
                  }}
                  withCheckIcon={false}
                  rightSection={<ArrowSvg />}
                  allowDeselect={false}
                  comboboxProps={{
                    position: 'bottom',
                    middlewares: { flip: false, shift: false },
                    offset: 4,
                  }}
                />
              )}
            </div>
            <OrderAmountErrorMessage
              message={orderAmountMessage}
              className={`${styles.errorPlaceholder} ${styles.amount_error}`}
            />
          </div>
        </div>
        {!infoOrderDetail.is_unit_based ? (
          <>
            <Group gap='8px'>
              <Switch
                color='#002CFB'
                styles={{
                  label: {
                    fontSize: '16px',
                    lineHeight: '22px',
                    color: '#121922',
                    cursor: 'pointer',
                  },
                  track: {
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: infoOrderDetail.add_tax_amount
                      ? '#002CFB'
                      : 'rgba(173, 191, 223, 0.40)',
                  },
                  thumb: {
                    border: 'none',
                  },
                }}
                checked={infoOrderDetail.add_tax_amount}
                onChange={() => {
                  handleTogglePercent();
                }}
                label='Добавить 6%'
              />
              <AppTooltip
                label={
                  infoOrderDetail.unit_price
                    ? `Мы добавим такую сумму, которая после выплаты 6% налога, составит ${infoOrderDetail.unit_price} ₽`
                    : `Мы добавим такую сумму, которая после выплаты 6% налога составит введенную`
                }>
                <InfoIcon style={{ cursor: 'pointer' }} />
              </AppTooltip>
            </Group>
            <Group gap='8px' mt='4px' style={{ cursor: 'pointer' }}>
              <Switch
                color='#002CFB'
                styles={{
                  label: {
                    fontSize: '16px',
                    lineHeight: '22px',
                    color: '#121922',
                    cursor: 'pointer',
                  },
                  track: {
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor:
                      infoOrderDetail.accept_automatically === 1
                        ? '#002CFB'
                        : 'rgba(173, 191, 223, 0.40)',
                  },
                  thumb: {
                    border: 'none',
                  },
                }}
                checked={infoOrderDetail.accept_automatically === 1}
                onChange={(event) => {
                  handleFieldChange(
                    'accept_automatically',
                    event.currentTarget.checked ? 1 : 0,
                  );
                }}
                label='Завершить заказ автоматически'
              />
              <AppTooltip
                label={
                  'После завершения заказа исполнителем автоматически будет подписан акт и отправлена выплата'
                }>
                <InfoIcon style={{ cursor: 'pointer' }} />
              </AppTooltip>
            </Group>
            <div>
              <div className={styles.order_inner__amount}>
                <p>{addSpaceNumber(sumToBePaid || 0)} ₽</p>
                <AppTooltip
                  label={`Цена указана с учетом комиссии компании ${infoPercent.commission_percent}%`}>
                  <InfoIcon style={{ cursor: 'pointer' }} />
                </AppTooltip>
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <div className={styles.amount_minmax}>
                <div>
                  <p className={styles.volumeLabel}>
                    <span className={styles.volumeLabel_full}>Минимальный объем</span>
                    <span className={styles.volumeLabel_short}>Мин. объем</span>
                  </p>
                  <NumberInput
                    flex={1}
                    placeholder='1'
                    allowNegative={false}
                    decimalScale={0}
                    hideControls
                    value={infoOrderDetail.unit_volume_min || '1'}
                    onChange={(val) => {
                      changeField('unit_volume_min', String(val));
                    }}
                    onBlur={() => syncUnitVolumeRangeFromMin()}
                    min={1}
                    rightSectionPointerEvents='auto'
                    rightSection={
                      <QuantityStepperControls
                        onIncrement={() => {
                          const newMin = Number(infoOrderDetail.unit_volume_min || 1) + 1;
                          changeField('unit_volume_min', String(newMin));
                          syncUnitVolumeRangeFromMin(newMin);
                        }}
                        onDecrement={() => {
                          const current = Number(infoOrderDetail.unit_volume_min || 1);
                          if (current <= 1) return;
                          const newMin = current - 1;
                          changeField('unit_volume_min', String(newMin));
                          syncUnitVolumeRangeFromMin(newMin);
                        }}
                        decrementDisabled={
                          Number(infoOrderDetail.unit_volume_min || '1') <= 1
                        }
                      />
                    }
                    styles={{
                      input: {
                        height: '46px',
                        fontSize: '16px',
                        border: 'none',
                      },
                      error: { fontSize: '14px', color: '#e10d0d' },
                    }}
                  />
                </div>

                <div>
                  <p className={styles.volumeLabel}>
                    <span className={styles.volumeLabel_full}>Максимальный объем</span>
                    <span className={styles.volumeLabel_short}>Макс. объем</span>
                  </p>
                  <NumberInput
                    flex={1}
                    placeholder='0'
                    allowNegative={false}
                    decimalScale={1}
                    hideControls
                    value={infoOrderDetail.unit_volume_max || ''}
                    onChange={(val) => {
                      changeField('unit_volume_max', String(val));
                    }}
                    onBlur={() => syncUnitVolumeRangeFromMax()}
                    min={0}
                    rightSectionPointerEvents='auto'
                    rightSection={
                      <QuantityStepperControls
                        onIncrement={() => {
                          const newMax =
                            infoOrderDetail.unit_volume_max === ''
                              ? 1
                              : Number(infoOrderDetail.unit_volume_max) + 1;
                          changeField('unit_volume_max', String(newMax));
                          syncUnitVolumeRangeFromMax(newMax);
                        }}
                        onDecrement={() => {
                          if (infoOrderDetail.unit_volume_max === '') return;

                          const current = Number(infoOrderDetail.unit_volume_max);
                          if (current <= 0) return;

                          if (current === 1) {
                            changeField('unit_volume_max', '');
                            return;
                          }

                          const newMax = current - 1;
                          changeField('unit_volume_max', String(newMax));
                          syncUnitVolumeRangeFromMax(newMax);
                        }}
                        decrementDisabled={
                          !infoOrderDetail.unit_volume_max ||
                          Number(infoOrderDetail.unit_volume_max) <= 0
                        }
                      />
                    }
                    styles={{
                      input: {
                        height: '46px',
                        fontSize: '16px',
                        border: errors.max_value ? '1px solid #e10d0d' : 'none',
                      },
                      error: { fontSize: '14px', color: '#e10d0d' },
                    }}
                  />
                </div>
              </div>
              {errors.max_value && <TextError text={errors.max_value} />}
            </div>

            <div className={styles.order_inner__amount}>
              <p>
                {addSpaceNumber(Number(infoOrderDetail.unit_price) || 0)} ₽/
                {transformedUnitMeasuresData?.find(
                  (item) =>
                    item.label === infoOrderDetail.unit_measure ||
                    item.value === infoOrderDetail.unit_measure?.toString(),
                )?.label ??
                  transformedUnitMeasuresData[0]?.label ??
                  null}
              </p>
            </div>
          </>
        )}
      </div>

      {showNotification && (
        <SuccessErrorInfo
          text='Шаблон успешно сохранен'
          type='success'
          handleClose={() => setShowNotification(false)}
        />
      )}
      {showSaveTemplateModal && (
        <Modal
          open={showSaveTemplateModal}
          setOpen={(open) => {
            setShowSaveTemplateModal(open);
            if (!open) {
              setError(false);
              setTemplateName('');
            }
          }}>
          <div className={styles.saveTemplateModal}>
            <h3 className={styles.saveTemplateModal__title}>
              Сохранить условия заказа в шаблон?
            </h3>
            <div>
              <Label text='Название шаблона' />
              <TextInput
                maxLength={30}
                placeholder='Введи название'
                value={templateName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTemplateName(e.target.value);
                  if (templateName.trim()) {
                    setError(false);
                  }
                }}
                styles={{
                  input: {
                    fontSize: '16px',
                    height: '48px',
                    border: `${
                      error || templateNameExists ? '1px solid #e10d0d' : 'none'
                    }`,
                  },
                  root: {
                    backgroundColor: 'white',
                  },
                }}
                required
              />
              {templateNameExists && (
                <TextError text='Шаблон с таким названием уже существует' />
              )}
              {error && <TextError text={'Название не может быть пустым'} />}
            </div>
            <div>
              <Button
                className={styles.saveTemplateModal__button}
                theme='info'
                onClick={() => {
                  if (!templateName.trim()) {
                    setError(true);
                    return;
                  }
                  if (isLoadingCreate) {
                    return;
                  }
                  handleSaveTemplate();
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Сохранить
                  {isLoadingCreate ? (
                    <Loader size='xs' color='white' />
                  ) : (
                    <IconCheckSvg />
                  )}
                </div>
              </Button>
              {errorSaveTemplate && <TextError text={errorSaveTemplate} />}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export const WithdrawLabel = () => {
  return (
    <div className={styles.withdraw_label}>
      <span className={styles.withdraw_label__description}>
        Данная опция позволяет не дожидаться ответа ФНС о регистрации чека и сразу
        отправить средства исполнителю.
      </span>
      <span className={styles.withdraw_label__info}>
        Стоимость услуги +1% к тарифу, но минимум 100 Р за заказ.
      </span>
    </div>
  );
};
